/**
 * Cliente de la API. Rutas relativas siempre: en el clúster la API y la SPA
 * comparten dominio, y en local lo resuelve el proxy de Vite.
 *
 * Los tipos siguen la forma real del informe que produce el motor, no una
 * versión aplanada: es la que consumirá el informe completo en H7.
 *
 * En H4 este fichero lo sustituye un cliente generado desde el OpenAPI.
 */

async function get<T>(path: string): Promise<T> {
  const response = await fetch(path);
  if (!response.ok) throw new Error(`${path} respondió ${response.status}`);
  return response.json() as Promise<T>;
}

export interface DemoProfile {
  id: string;
  title: string;
  blurb: string;
}

export interface Dimension {
  key: string;
  label: string;
  score: number;
  zone?: string;
  icon?: string;
}

export interface DissonanceItem {
  code: string;
  label: string;
  severity_adjusted: number;
  band: string;
  evidence: string[];
}

export interface Report {
  profile_title: string;
  profile_subtitle: string;
  ipc: {
    I: number;
    ID: number;
    F: number;
    V: number;
    P: number;
    IPC_final: number;
    ipc_band: string;
    guardrails_applied: string[];
  };
  identity_professional: {
    dominant: { key?: string; label?: string; score?: number } | null;
    secondary: { key?: string; label?: string } | null;
    isDual: boolean;
    comboLabel: string | null;
  };
  interests_fit: { code: string; top_dims: Dimension[]; environments_fit: string[] };
  value_mobilization: { ivm_global: number; band: string; coherence_flags: string[] };
  brakes_analysis: { global: number; main_brake: { label?: string; score?: number } | null };
  transition_readiness: {
    index: number;
    readiness_label: string;
    dimensions: Dimension[];
    main_limiter: Dimension | null;
    next_step: string | null;
    pulse: { overall: string; modifiers: string[] };
  };
  dissonances_analysis: {
    idd_global: number;
    idd_band: string;
    active_count: number;
    top_active: DissonanceItem[];
  };
  constraints_and_viability: {
    feasibility_score: number;
    feasibility_band: string;
    feasibility_band_label: string;
    feasibility_warnings: string[];
  };
  integrated_synthesis: {
    diagnostic_priority: { priority: string; reason: string };
    pattern_summary: string;
  };
  recommended_actions: {
    priorities: { rank: number; label: string; why: string; what: string }[];
    next_step: string;
  };
  plan_7_30_90: { plan_7: string[]; plan_30: string[]; plan_90: string[] };
  palancas_profesionales: {
    global_activation_index: number;
    aspiration_index: number;
    global_gap_index: number;
    variables: { key: string; shortLabel: string; current: number; desired: number; gap: number }[];
    patterns: { key: string; label: string; description: string }[];
  } | null;
  ipl: { ipl: number; band: string; main_asset: string; main_block: string };
  scenarios: {
    id: string;
    label: string;
    fit_probability: number;
    opportunity: string;
    risk: string;
  }[];
}

export interface Strategic {
  decision: { level: string; label: string; tagline: string; confidence: number };
  discard: { code: string; label: string; reason: string; priority: string }[];
  questions: { category: string; q: string; urgency: string }[];
}

export interface Decision {
  status: string;
  dominant_tension: { code: string; label: string; description: string };
  decision_pattern: { code: string; label: string; recommendation: string };
  main_professional_question: string;
  confidence_score: number;
}

export interface Narrative {
  titular: string;
  subtitulo: string;
}

export interface Diagnosis {
  method_version: string;
  scoring_version: string;
  report: Report;
  strategic: Strategic;
  decision: Decision;
  narrative: Narrative;
  narrative_source: string;
}

export const api = {
  demoProfiles: () => get<DemoProfile[]>("/api/v1/demo/profiles"),
  demoScore: (id: string) => get<Diagnosis>(`/api/v1/demo/profiles/${id}/score`),
};
